import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useSEO } from '../hooks/useSEO';
import { FooterSection } from '../components/landing';

const AiRetrospectiveSummaryPage: React.FC = () => {
  useSEO({
    title: 'AI Retrospective Summary Generator — Instant Sprint Reports',
    description:
      'Turn your sprint retrospective notes into an executive-ready AI report in seconds. Themes, risks, decisions, and action items — automatically generated from your Retrosive board.',
    canonical: '/ai-retrospective-summary',
    ogType: 'article',
    article: {
      publishedTime: '2026-03-01',
      author: 'Retrosive Team',
      tags: [
        'AI retrospective summary',
        'sprint retrospective report',
        'AI agile report',
        'retrospective automation',
        'sprint summary generator',
      ],
    },
  });

  // JSON-LD structured data
  useEffect(() => {
    const existingScript = document.getElementById('ai-retro-jsonld');
    if (existingScript) existingScript.remove();

    const script = document.createElement('script');
    script.id = 'ai-retro-jsonld';
    script.type = 'application/ld+json';
    script.text = JSON.stringify([
      {
        '@context': 'https://schema.org',
        '@type': 'SoftwareApplication',
        name: 'Retrosive AI Report Generator',
        applicationCategory: 'BusinessApplication',
        description:
          'AI-powered retrospective summary generator that transforms sprint retro boards into executive-ready reports with themes, risks, decisions, and action items.',
        url: 'https://retrosive.com/ai-retrospective-summary',
        offers: {
          '@type': 'Offer',
          price: '0',
          priceCurrency: 'USD',
          description: '2 AI reports per month on the free plan',
        },
        operatingSystem: 'Web',
        screenshot: 'https://retrosive.com/og-image.png',
      },
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: [
          {
            '@type': 'Question',
            name: 'What is an AI retrospective summary?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'An AI retrospective summary automatically analyzes sprint retro discussions and generates a structured report including themes, risks, decisions, and action items — without any manual formatting.',
            },
          },
          {
            '@type': 'Question',
            name: 'How do you write a sprint retrospective summary?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Traditionally, writing a sprint retrospective summary requires reading every sticky note, grouping themes manually, extracting decisions, identifying risks, and formatting it all into a report. Retrosive automates this entire process in one click using AI.',
            },
          },
          {
            '@type': 'Question',
            name: 'Can AI generate retrospective reports?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Yes. Retrosive AI analyzes your board data — cards, votes, and actions — and generates a structured retrospective report with an executive summary, key themes, decisions, risks, and prioritized action items.',
            },
          },
        ],
      },
    ]);
    document.head.appendChild(script);

    return () => {
      const s = document.getElementById('ai-retro-jsonld');
      if (s) s.remove();
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">

      {/* ── Hero ─────────────────────────────────────────────── */}
      <section className="bg-gradient-to-br from-slate-900 via-orange-950 to-slate-900 py-24 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-orange-500/20 text-orange-300 text-sm font-semibold mb-6 border border-orange-500/30">
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" /></svg>
            AI-Powered
          </span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
            Get an AI Summary of Your<br />
            <span className="text-orange-400">Sprint Retrospective — Instantly</span>
          </h1>
          <p className="text-xl text-slate-300 mb-4 max-w-2xl mx-auto">
            Paste your retro notes or run it live. Retrosive AI extracts themes, action items, and team sentiment in under 30 seconds.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mt-8">
            <Link
              to="/tools/ai-retrospective-report-generator"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-orange-500 hover:bg-orange-400 text-white font-bold text-lg rounded-xl hover:shadow-xl hover:scale-105 transition-all duration-300"
            >
              Try Free — No Signup Required
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
            </Link>
            <a
              href="#example-output"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white/10 text-white font-semibold text-lg rounded-xl hover:bg-white/20 transition-all duration-300 border border-white/20"
            >
              See Example Output
            </a>
          </div>
          <div className="mt-8 flex flex-wrap gap-6 justify-center text-sm text-slate-400">
            {['Theme detection', 'Action item extraction', 'Sentiment analysis', 'Export ready'].map((f) => (
              <span key={f} className="flex items-center gap-1.5">
                <svg className="w-4 h-4 text-orange-400" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" /></svg>
                {f}
              </span>
            ))}
          </div>
          <p className="text-slate-500 text-sm mt-4">2 AI reports/month free · No credit card required</p>
        </div>
      </section>

      {/* ── Section 1: Problem ───────────────────────────────── */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-14">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Why sprint retros lose impact
            </h2>
            <p className="text-gray-500 text-lg">
              Most retros end without the insights ever reaching the people who need them.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: '📝', text: 'Scattered notes nobody reads after the meeting' },
              { icon: '📉', text: 'No clear summary lands with leadership' },
              { icon: '📌', text: 'Decisions buried deep in sticky notes' },
              { icon: '⚠️', text: 'Risks go unnoticed until they become blockers' },
              { icon: '🔁', text: 'Action items forgotten by next sprint' },
              { icon: '🔍', text: 'Recurring problems never surfaced as patterns' },
            ].map(({ icon, text }) => (
              <div
                key={text}
                className="flex items-start gap-4 bg-white rounded-xl p-6 border border-gray-200 shadow-sm"
              >
                <span className="text-3xl flex-shrink-0">{icon}</span>
                <p className="text-gray-700 font-medium">{text}</p>
              </div>
            ))}
          </div>
          <div className="mt-10 text-center space-y-2 text-gray-500 text-lg font-medium">
            <p>Leaders don't read raw boards.</p>
            <p>Teams forget recurring problems.</p>
            <p className="text-orange-700 font-semibold">Insights disappear after the meeting.</p>
          </div>
        </div>
      </section>

      {/* ── Section 2: Solution ─────────────────────────────── */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-14">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Generate a clear AI retrospective report instantly
            </h2>
            <p className="text-gray-500 text-lg max-w-2xl mx-auto">
              Retrosive AI automatically transforms your board into a structured, shareable report.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: '📋', title: 'Executive summary', desc: 'High-level narrative ready to forward to stakeholders in one click' },
              { icon: '🏷️', title: 'Key themes', desc: 'Grouped insights that reveal what the team is really experiencing' },
              { icon: '✅', title: 'Decisions taken', desc: 'Clearly documented so nothing gets lost between meetings' },
              { icon: '⚠️', title: 'Identified risks', desc: 'Surfaced proactively with severity so leadership can act fast' },
              { icon: '🎯', title: 'Prioritized action items', desc: 'Top actions ranked by impact — not just a flat list' },
            ].map(({ icon, title, desc }) => (
              <div
                key={title}
                className="group relative bg-gradient-to-br bg-orange-50 rounded-2xl p-6 border border-orange-100 hover:border-orange-300 hover:shadow-lg transition-all duration-300"
              >
                <div className="text-4xl mb-3 group-hover:scale-110 transition-transform">{icon}</div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">{title}</h3>
                <p className="text-gray-600 text-sm">{desc}</p>
              </div>
            ))}
          </div>
          <div className="mt-12 text-center text-gray-400 text-lg space-y-1">
            <p>No manual formatting.</p>
            <p>No messy exports.</p>
            <p className="text-orange-600 font-semibold">No rewriting notes.</p>
          </div>
        </div>
      </section>

      {/* ── Section 3: How It Works ─────────────────────────── */}
      <section className="py-20 px-4 bg-gradient-to-br from-orange-500 via-orange-600 to-orange-500">
        <div className="max-w-5xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">How it works</h2>
          <p className="text-white/80 mb-14 text-lg">Three steps. Zero friction.</p>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                step: '1',
                title: 'Run your sprint retrospective',
                desc: 'Use any template — Start/Stop/Continue, 4Ls, Mad/Sad/Glad, and more.',
                icon: '🔄',
              },
              {
                step: '2',
                title: 'Click "Generate AI Report"',
                desc: 'Our AI analyzes cards, votes, and actions in your board in seconds.',
                icon: '✨',
              },
              {
                step: '3',
                title: 'Share or export',
                desc: 'Get a structured report ready to send to your CTO or stakeholders.',
                icon: '📤',
              },
            ].map(({ step, title, desc, icon }) => (
              <div key={step} className="relative">
                {/* Connector line */}
                <div className="hidden md:block absolute top-10 left-full w-full h-0.5 bg-orange-700/50 -translate-x-1/2 z-0" />
                <div className="relative z-10 bg-white/10 border border-white/20 rounded-2xl p-8 backdrop-blur-sm hover:bg-white/15 transition-all duration-300">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-orange-400 to-orange-500 flex items-center justify-center text-white font-bold text-xl mx-auto mb-4">
                    {step}
                  </div>
                  <div className="text-4xl mb-3">{icon}</div>
                  <h3 className="text-xl font-bold text-white mb-2">{title}</h3>
                  <p className="text-white/80 text-sm">{desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Section 4: Example Output ───────────────────────── */}
      <section id="example-output" className="py-20 px-4 bg-gray-50">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Example AI report output</h2>
            <p className="text-gray-500 text-lg">This is what gets generated from a typical sprint retro board.</p>
          </div>
          <div className="bg-white rounded-2xl border border-gray-200 shadow-xl overflow-hidden">
            {/* Report header */}
            <div className="bg-gradient-to-r from-orange-500 to-orange-700 px-8 py-5 flex items-center gap-3">
              <span className="text-2xl">✨</span>
              <div>
                <p className="text-orange-100 text-xs uppercase tracking-widest font-semibold">AI Report · Sprint 42</p>
                <p className="text-white font-bold text-lg">Retrospective Summary</p>
              </div>
            </div>
            <div className="px-8 py-6 space-y-6">
              {/* Executive Summary */}
              <div>
                <h3 className="text-xs uppercase tracking-widest font-bold text-orange-600 mb-2">Executive Summary</h3>
                <p className="text-gray-700 leading-relaxed">
                  This sprint showed strong team collaboration but recurring issues around unclear requirements and last-minute changes.
                  Engineering velocity improved slightly, but blockers remain.
                </p>
              </div>
              <hr className="border-gray-100" />
              {/* Key Themes */}
              <div>
                <h3 className="text-xs uppercase tracking-widest font-bold text-orange-600 mb-3">Key Themes</h3>
                <div className="flex flex-wrap gap-2">
                  {['Requirement clarity', 'Review process delays', 'Async communication improvements'].map((t) => (
                    <span key={t} className="px-3 py-1 rounded-full bg-orange-50 text-orange-700 text-sm font-medium border border-orange-100">
                      {t}
                    </span>
                  ))}
                </div>
              </div>
              <hr className="border-gray-100" />
              {/* Risks */}
              <div>
                <h3 className="text-xs uppercase tracking-widest font-bold text-amber-600 mb-3">Risks Identified</h3>
                <div className="space-y-2">
                  <div className="flex items-center gap-3">
                    <span className="px-2 py-0.5 rounded text-xs font-bold bg-red-100 text-red-700">HIGH</span>
                    <span className="text-gray-700 text-sm">Repeated scope changes destabilizing sprint goals</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="px-2 py-0.5 rounded text-xs font-bold bg-amber-100 text-amber-700">MEDIUM</span>
                    <span className="text-gray-700 text-sm">Dependency bottlenecks causing delivery delays</span>
                  </div>
                </div>
              </div>
              <hr className="border-gray-100" />
              {/* Recommended Actions */}
              <div>
                <h3 className="text-xs uppercase tracking-widest font-bold text-emerald-600 mb-3">Recommended Actions</h3>
                <ul className="space-y-2">
                  {[
                    'Create requirement validation checklist before sprint planning',
                    'Assign clear ticket owner before sprint kickoff',
                    'Review dependency mapping in weekly sync',
                  ].map((action) => (
                    <li key={action} className="flex items-start gap-2 text-sm text-gray-700">
                      <span className="text-emerald-500 mt-0.5">✓</span>
                      {action}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Section 5: Why Not ChatGPT ──────────────────────── */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-14">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Why not just use ChatGPT?
            </h2>
            <p className="text-gray-500 text-lg max-w-xl mx-auto">
              Generic AI tools are powerful but not built for retrospectives. There's a difference.
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            {/* Generic AI */}
            <div className="rounded-2xl border-2 border-gray-200 p-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center text-xl">🤖</div>
                <h3 className="text-xl font-bold text-gray-900">Generic AI tools</h3>
              </div>
              <ul className="space-y-3">
                {[
                  "Don't understand retro structure or phases",
                  "Don't connect actions to themes automatically",
                  "Don't track patterns across multiple sprints",
                  'Require manual copy-paste of board content',
                  'No context on votes or team priorities',
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3 text-gray-600">
                    <span className="text-red-400 mt-0.5 flex-shrink-0">✕</span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            {/* Retrosive */}
            <div className="rounded-2xl border-2 border-orange-400 p-8 bg-gradient-to-br bg-orange-50 shadow-lg">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center text-xl">⚡</div>
                <h3 className="text-xl font-bold text-gray-900">Retrosive</h3>
              </div>
              <ul className="space-y-3">
                {[
                  'Structured board data — no copy-paste needed',
                  'Action tracking built-in and linked to themes',
                  'AI designed specifically for agile retrospectives',
                  'Trend detection across retros over time',
                  'Vote weight considered in prioritization',
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3 text-gray-700">
                    <span className="text-orange-500 mt-0.5 flex-shrink-0">✓</span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <p className="text-center text-gray-500 mt-10 text-lg">
            This isn't a text summarizer.{' '}
            <span className="text-orange-700 font-semibold">It's a retrospective intelligence layer.</span>
          </p>
        </div>
      </section>

      {/* ── Section 6: SEO FAQ ───────────────────────────────── */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 mb-10 text-center">Common questions</h2>
          <div className="space-y-6">
            <div className="bg-white rounded-xl p-7 border border-gray-200 shadow-sm">
              <h3 className="text-lg font-bold text-gray-900 mb-3">
                What is an AI retrospective summary?
              </h3>
              <p className="text-gray-600 leading-relaxed">
                An AI retrospective summary automatically analyzes sprint retro discussions and generates a structured
                report including themes, risks, and actions — saving facilitators and scrum masters hours of manual work after
                every sprint.
              </p>
            </div>
            <div className="bg-white rounded-xl p-7 border border-gray-200 shadow-sm">
              <h3 className="text-lg font-bold text-gray-900 mb-3">
                How do you write a sprint retrospective summary?
              </h3>
              <p className="text-gray-600 mb-4 leading-relaxed">Normally, writing a retro summary means:</p>
              <ul className="space-y-2 mb-4">
                {[
                  'Reading every sticky note on the board',
                  'Grouping themes manually',
                  'Extracting decisions from discussion',
                  'Identifying and prioritizing risks',
                  'Formatting everything into a shareable report',
                ].map((step) => (
                  <li key={step} className="flex items-start gap-2 text-gray-600">
                    <span className="text-gray-400 mt-0.5">—</span>
                    {step}
                  </li>
                ))}
              </ul>
              <p className="text-orange-700 font-semibold">Retrosive automates this entire process in one click.</p>
            </div>
            <div className="bg-white rounded-xl p-7 border border-gray-200 shadow-sm">
              <h3 className="text-lg font-bold text-gray-900 mb-3">
                Can AI generate retrospective reports automatically?
              </h3>
              <p className="text-gray-600 leading-relaxed">
                Yes — when the AI has access to structured board data. Retrosive's AI reads your board cards, vote counts,
                and action items to generate a prioritized, executive-ready report. You don't need to copy anything — it
                connects directly to your board.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── Section 7: Final CTA ────────────────────────────── */}
      <section className="py-24 px-4 bg-gradient-to-r from-orange-500 to-orange-600">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-4xl font-bold text-white mb-4">
            Ready to turn your retros into real outcomes?
          </h2>
          <p className="text-white/80 text-lg mb-10">
            2 AI reports per month — no credit card required.
          </p>
          <Link
            to="/register"
            className="inline-flex items-center gap-2 px-10 py-5 bg-white text-orange-700 font-bold text-xl rounded-xl hover:shadow-2xl hover:scale-105 transition-all duration-300"
          >
            Generate Your Free AI Report
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
            </svg>
          </Link>
        </div>
      </section>

      <FooterSection />
    </div>
  );
};

export default AiRetrospectiveSummaryPage;
